// =============================================================================
// VUE INSTALLATEUR — Fiche Dossier dédiée
// =============================================================================
// Focus : Client, projet, planning chantier, photos, documents
// Pas de finance TEF, pas d'agent IA, pas de conformité avancée
// =============================================================================

import {
  ArrowLeft, User, Home, Wrench, Camera, FolderOpen,
  MapPin, Phone, Mail, Calendar, CheckCircle2, Clock,
  FileText, Shield, ChevronDown, ChevronUp, RefreshCw, Loader2,
  Upload, Receipt
} from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { PipelineStepper, getPipelineStep, resolveStepCode } from "./PipelineStepper";
import { PhotosForm } from "./PhotosForm";
import { DocumentsForm } from "./DocumentsForm";
import { formatMontant, API, getAuthHeaders } from "./helpers";
import { toast } from "sonner";
import PhotosChantier from "../PhotosChantier";
import PlanningChantier from "../../operation/PlanningChantier";
import CommandesManager from "../../operation/CommandesManager";
import { formatClientAddress, formatClientFullName } from "../../../utils/clientFormat";
// CC-DEV1-32 — statut proéminent (source unique = governance_pipeline via
// usePipeline) + suivi Liaison ANAH (composant AMO réutilisé en lecture seule).
import usePipeline from "../../../hooks/usePipeline";
import AnahLinkTab from "../../../components/metier/AnahLinkTab";

function InfoField({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--oh-text-muted)" }} />}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>{label}</p>
        <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)" }}>{value}</p>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${open ? color + "60" : "var(--oh-border)"}` }} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3"
        style={{ background: open ? `${color}10` : "var(--oh-bg-elevated)", borderBottom: open ? `1px solid ${color}30` : "none" }}
      >
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full" style={{ background: color }} />
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
      </button>
      {open && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

// ─── Upload Devis / Facture Installateur ───
const TRAVAUX_STATUTS = ["travaux_planifies", "travaux_en_cours", "travaux_termines", "chantier_a_planifier", "chantier_planifie", "chantier_en_cours", "chantier_termine", "paye", "aides_validees"];

function InstallateurUploadButton({ dossier, dossierId, tenantId, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isTravaux = TRAVAUX_STATUTS.includes(dossier.statut);
  const type = isTravaux ? "facture" : "devis";
  const label = isTravaux ? "Déposer ma facture" : "Déposer mon devis";
  const IconUp = isTravaux ? Receipt : Upload;
  const color = isTravaux ? "#10b981" : "var(--oh-stat-blue-text)";

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);
      formData.append("subfolder", `05_TRAVAUX/${type}`);
      const headers = {};
      const token = localStorage.getItem("session_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      headers["X-Tenant-ID"] = tenantId || "tef";

      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/documents/upload`, {
        method: "POST",
        headers,
        credentials: "include",
        body: formData,
      });
      if (res.ok) {
        toast.success(`${type === "devis" ? "Devis" : "Facture"} déposé(e) avec succès`);
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur lors du dépôt`);
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: `1px solid ${color}30` }} data-testid={`installateur-upload-${type}`}>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={handleUpload} />
      <Button
        className="w-full text-sm font-medium py-3"
        style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`btn-upload-${type}`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <IconUp className="w-4 h-4 mr-2" />}
        {uploading ? "Envoi en cours..." : label}
      </Button>
      <p className="text-[10px] text-center mt-2" style={{ color: "var(--oh-text-muted)" }}>
        {type === "devis" ? "PDF ou image — sera vérifié par rapport au DAL" : "PDF ou image — sera transmis à Stéphanie"}
      </p>
    </div>
  );
}

export function DossierInstallateurView({ dossier, dossierId, tenantId, documents, onBack, onUpdate }) {
  // Hook appelé AVANT tout early-return (règle des hooks React).
  const { pipeline } = usePipeline();
  if (!dossier) return null;

  const client = dossier.client || {};
  const logement = dossier.infos_logement || {};
  const clientNom = formatClientFullName(client) || dossier.client_nom || "—";
  const adresseClient = formatClientAddress(client) || logement.adresse || "";
  const pipelineStep = getPipelineStep(dossier.statut);
  const isChantiableStatus = ["chantier_a_planifier","chantier_planifie","chantier_en_cours","chantier_termine","travaux_planifies","travaux_en_cours","travaux_termines"].includes(dossier.statut);

  // CC-DEV1-32 — statut "où en est le dossier" (besoin n°1). ANAH fait foi après
  // dépôt (libellé du snapshot) ; sinon libellé de l'étape pipeline courante.
  const stepCode = pipeline ? resolveStepCode(dossier.statut, dossier, pipeline) : null;
  const currentStep = pipeline?.steps?.find((s) => s.code === stepCode);
  const statutLabel = (dossier.anah_linked && dossier.anah_snapshot?.statut_label)
    ? dossier.anah_snapshot.statut_label
    : (currentStep?.label || "En cours");

  return (
    <div className="space-y-4" data-testid="dossier-installateur-view">
      {/* Header compact */}
      <div
        className="sticky top-0 z-40 px-4 py-3 backdrop-blur-xl"
        style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)" }}
      >
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="installateur-back-btn">
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)", fontFamily: "'IBM Plex Mono', monospace" }}>
              {dossier.référence}
            </p>
            <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>{clientNom}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onUpdate} data-testid="installateur-refresh-btn">
            <RefreshCw className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
        </div>
        <PipelineStepper statut={dossier.statut} compact />
      </div>

      {/* CC-DEV1-32 — Statut proéminent (besoin n°1 : "où en est le dossier") */}
      <div
        className="rounded-lg p-4 flex items-center gap-3"
        data-testid="installateur-statut-hero"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", borderLeft: "4px solid var(--oh-stat-blue-text)" }}
      >
        <Shield className="w-6 h-6 shrink-0" style={{ color: "var(--oh-stat-blue-text)" }} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Statut actuel du dossier</p>
          <p className="text-xl font-medium truncate" style={{ color: "var(--oh-text)" }} data-testid="installateur-statut-label">{statutLabel}</p>
        </div>
        {dossier.anah_linked && dossier.anah_reference && (
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Réf. ANAH</p>
            <p className="text-sm font-mono" style={{ color: "var(--oh-text)" }}>{dossier.anah_reference}</p>
          </div>
        )}
      </div>

      {/* Carte Client + Logement */}
      <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="installateur-client-card">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4" style={{ color: "var(--oh-hub-commercial)" }} />
          <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Client & Logement</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <InfoField label="Nom" value={clientNom} icon={User} />
          <InfoField label="Téléphone" value={client.telephone || dossier.telephone} icon={Phone} />
          <InfoField label="Email" value={client.email} icon={Mail} />
          <InfoField label="Adresse" value={adresseClient} icon={MapPin} />
          <InfoField label="Type logement" value={logement.type_logement || logement.type} icon={Home} />
          <InfoField label="Type projet" value={dossier.type_projet} icon={Wrench} />
        </div>
      </div>

      {/* Statut synthèse */}
      <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="installateur-status-card">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" />
          <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Statut Dossier</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Visite</p>
            <p className="text-sm" style={{ color: "var(--oh-text)" }}>{dossier.date_visite || "Non planifiée"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Visiteur AMO</p>
            <p className="text-sm" style={{ color: "var(--oh-text)" }}>{dossier.visiteur_amo || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Dépôt ANAH</p>
            <p className="text-sm" style={{ color: "var(--oh-text)" }}>{dossier.date_depot_anah || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Octroi</p>
            <p className="text-sm" style={{ color: "var(--oh-text)" }}>{dossier.date_octroi || "—"}</p>
          </div>
        </div>
      </div>

      {/* CC-DEV1-32 — Liaison ANAH (suivi/synchro) : composant AMO réutilisé en
          lecture seule (l'installateur suit, ne modifie pas la liaison). */}
      <Section title="Liaison ANAH" icon={Shield} color="var(--oh-stat-cyan-text)" defaultOpen={false} testId="installateur-section-anah">
        <AnahLinkTab dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} readOnly />
      </Section>

      {/* Montants simplifiés */}
      <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="installateur-montants">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Devis</p>
            <p className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>{formatMontant(dossier.montant_devis)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Aides</p>
            <p className="text-lg font-medium text-[var(--oh-success-text)]">{formatMontant(dossier.montant_prime)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase" style={{ color: "var(--oh-text-muted)" }}>Reste à charge</p>
            <p className="text-lg font-medium" style={{ color: "var(--oh-stat-amber-text)" }}>{formatMontant(dossier.reste_a_charge)}</p>
          </div>
        </div>
      </div>

      {/* Bouton Déposer Devis ou Facture */}
      <InstallateurUploadButton dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={onUpdate} />

      {/* Chantier / Planning */}
      {isChantiableStatus && (
        <Section title="Chantier & Planning" icon={Calendar} color="var(--oh-hub-operation)" defaultOpen testId="installateur-section-chantier">
          <PlanningChantier dossierId={dossier.id} tenantId={tenantId} dossier={dossier} />
          <CommandesManager dossierId={dossier.id} tenantId={tenantId} devisSigne={true} />
        </Section>
      )}

      {/* Photos */}
      <Section title="Photos visite & chantier" icon={Camera} color="var(--oh-stat-amber-text)" defaultOpen={false} testId="installateur-section-photos">
        <PhotosForm dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille} onUpdate={onUpdate} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
        {isChantiableStatus && (
          <PhotosChantier dossierId={dossier.id} tenantId={tenantId} verrouille={dossier.verrouille} clientNom={client.nom || ""} dossierRef={dossier.référence} />
        )}
      </Section>

      {/* Documents */}
      <Section title={`Documents${documents?.length > 0 ? ` (${documents.length})` : ""}`} icon={FolderOpen} color="var(--oh-text-muted)" defaultOpen={false} testId="installateur-section-docs">
        {/* tenant = celui DU DOSSIER (super_admin cross-tenant : voir la GED + la
            liste de types du tenant du dossier, pas du tenant courant). */}
        <DocumentsForm dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} documents={documents || []} verrouille={dossier.verrouille} onUpdate={onUpdate} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
      </Section>
    </div>
  );
}
